import { Box, Button, useTheme, Tooltip, Theme } from '@mui/material';
import CalendarIcon from 'components/Icons/CalendarIcon';
import PremiumPill from 'components/Icons/PremiumPill';
import { useCallback } from 'react';
import { useRouter } from 'next/router';
import { trackEvent, getWindow } from '@surfline/web-common';
import { CLICKED_SUBSCRIBE_CTA } from '../../common/constants';
import styles from './HistoricViewToggleButton.module.scss';

const HistoricViewToggleButton = ({
  isHistoricView,
  toggleHistoricView,
  tooltipText,
  isPermitted,
  segmentPropertiesCTA,
}: {
  isHistoricView: boolean;
  toggleHistoricView: () => void;
  tooltipText: string;
  isPermitted?: boolean;
  segmentPropertiesCTA?: Record<string, any>;
}) => {
  const theme = useTheme();
  const router = useRouter();
  const isNative = router?.query?.native?.toString() === 'true';
  const upgradeUrl = isNative ? 'surfline://upgrade' : '/upgrade';

  const handleClick = useCallback(() => {
    if (!isPermitted) {
      trackEvent(CLICKED_SUBSCRIBE_CTA, {
        ...segmentPropertiesCTA,
      });
      getWindow()?.location.assign(upgradeUrl);
      return;
    }
    toggleHistoricView();
  }, [isPermitted, segmentPropertiesCTA, upgradeUrl, toggleHistoricView]);

  const calendarButtonStyles = useCallback(
    (t: Theme) => ({
      backgroundColor: isHistoricView ? t.palette.text.brandSecondary : t.palette.common.white,
      border: `1px solid ${t.palette.text.light}`,
      '&:hover, &:focus-visible': {
        backgroundColor: isHistoricView ? t.palette.text.brandSecondary : t.palette.text.light,
      },
      color: isHistoricView ? t.palette.common.white : t.palette.text.brandSecondary,
      height: '36px',
      width: 'auto',
    }),
    [isHistoricView],
  );

  return (
    <Box marginRight="8px">
      <Tooltip arrow enterTouchDelay={0} placement="top" title={tooltipText} disableTouchListener>
        <Box>
          <Button
            className="historic-view-toggle-button"
            data-testid="historic-view-toggle-button-with-text"
            onClick={handleClick}
            type="button"
            size="small"
            sx={calendarButtonStyles}
            startIcon={
              <CalendarIcon
                fill={
                  isHistoricView ? theme.palette.common.white : theme.palette.text.brandSecondary
                }
              />
            }
          >
            <span className={styles.inlineFlexCenter}>
              Historical
              {!isPermitted && (
                <span className={styles.pillContainer}>
                  <PremiumPill />
                </span>
              )}
            </span>
          </Button>
        </Box>
      </Tooltip>
    </Box>
  );
};

export default HistoricViewToggleButton;
